/**
 * Conta e segurança (docs/spec/settings.md): trocar a senha (diálogo) e cancelar a conta, com a
 * confirmação na própria linha. O servidor só apaga com a senha.
 */
import { useState } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { Button, Dialog, TextEdit } from '../ui/controls.js';
import { toast } from '../ui/Toaster.js';

export function PasswordDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const change = useApp((s) => s.changePassword);
  const busy = useApp((s) => s.busy);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const mismatch = again !== '' && next !== again;
  const ok = current !== '' && next.length >= 8 && next === again;
  const submit = async () => {
    if (!ok) return;
    if (await change(current, next)) {
      toast(t('config.account.passwordChanged'), 'success');
      onClose();
    }
  };
  return (
    <Dialog
      title={t('config.account.changePassword')}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" disabled={!ok} busy={busy} onClick={() => void submit()}>
            {t('config.account.changePassword')}
          </Button>
        </>
      }
    >
      <form
        className="rd-prefs__form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="rd-prefs__field">
          <span>{t('config.account.current')}</span>
          <TextEdit look="filled" type="password" value={current} onChange={setCurrent} autoComplete="current-password" autoFocusOnMount />
        </label>
        <label className="rd-prefs__field">
          <span>{t('config.account.new')}</span>
          <TextEdit look="filled" type="password" value={next} onChange={setNext} autoComplete="new-password" />
        </label>
        <label className="rd-prefs__field">
          <span>{t('config.account.again')}</span>
          <TextEdit look="filled" type="password" value={again} onChange={setAgain} autoComplete="new-password" invalid={mismatch} />
        </label>
        <p className="rd-prefs__desc">{mismatch ? t('config.account.mismatch') : t('config.account.rule')}</p>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

/** A confirmação de cancelar a conta, no lugar da linha: a senha e o botão de apagar. */
export function DeleteAccountConfirm({ onBack }: { onBack: () => void }) {
  const t = useT();
  const remove = useApp((s) => s.deleteAccount);
  const busy = useApp((s) => s.busy);
  const [password, setPassword] = useState('');
  return (
    <form
      className="rd-prefs__confirm"
      onSubmit={(e) => {
        e.preventDefault();
        if (password) void remove(password);
      }}
    >
      <p className="rd-prefs__confirmText" role="alert">
        <strong>{t('prefs.account.delete.confirm')}</strong> {t('config.account.deleteWarning')}
      </p>
      <label className="rd-prefs__field">
        <span>{t('config.account.confirmPassword')}</span>
        <TextEdit look="filled" type="password" value={password} onChange={setPassword} autoComplete="current-password" autoFocusOnMount />
      </label>
      <div className="rd-prefs__confirmActions">
        <Button variant="secondary" onClick={onBack}>
          {t('prefs.back')}
        </Button>
        <Button variant="action" className="rd-btn--danger" type="submit" disabled={!password} busy={busy}>
          {t('config.account.deleteForever')}
        </Button>
      </div>
    </form>
  );
}
